import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const hasPhoto = (item) => {
  if (typeof item.image_url !== "string" || item.image_url.trim().length === 0) {
    return false;
  }

  try {
    const imageUrl = new URL(item.image_url, window.location.origin);
    return imageUrl.protocol === "https:" || imageUrl.protocol === "http:";
  } catch {
    return false;
  }
};

/**
 * FeaturedRolls — homepage favorites selected in the admin menu catalog.
 * Every available item marked is_featured appears here until its photo fails to load.
 */
export default function FeaturedRolls() {
  const [items, setItems] = useState([]);
  const [failedImageIds, setFailedImageIds] = useState(() => new Set());

  const loadFavorites = useCallback(async () => {
    try {
      const response = await api.get("/menu");
      const favorites = response.data.filter(
        (item) => item.is_available && item.is_featured && hasPhoto(item),
      );
      setItems(favorites);
      setFailedImageIds(new Set());
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    loadFavorites();
    const refreshId = window.setInterval(loadFavorites, 30000);
    const refreshOnReturn = () => {
      if (document.visibilityState === "visible") loadFavorites();
    };
    document.addEventListener("visibilitychange", refreshOnReturn);
    return () => {
      window.clearInterval(refreshId);
      document.removeEventListener("visibilitychange", refreshOnReturn);
    };
  }, [loadFavorites]);

  const visibleItems = useMemo(
    () => items.filter((item) => !failedImageIds.has(item.id)),
    [failedImageIds, items],
  );

  const hideBrokenPhoto = (itemId) => {
    setFailedImageIds((current) => new Set([...current, itemId]));
  };

  if (visibleItems.length === 0) return null;

  return (
    <section className="py-20" data-testid="home-favorites-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow mb-2">— Our Menu —</p>
            <h2
              className="font-display font-bold text-4xl sm:text-5xl tracking-tight"
              data-testid="home-favorites-title"
            >
              Explore Our Favorites
            </h2>
            <p
              className="mt-3 max-w-xl leading-relaxed text-neutral-500"
              data-testid="home-favorites-description"
            >
              Hand-picked favorites from our menu, selected by the Sandar Sushi team.
            </p>
          </div>
          <Link to="/menu">
            <Button
              variant="outline"
              className="rounded-full"
              data-testid="home-favorites-see-all"
            >
              See All <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visibleItems.map((item) => (
            <Link
              key={item.id}
              to={`/menu?category=${encodeURIComponent(item.category)}`}
              className="group relative aspect-[4/3] overflow-hidden rounded-3xl bg-neutral-100"
              data-testid={`home-favorite-card-${item.id}`}
            >
              <img
                src={item.image_url}
                alt={item.name}
                loading="lazy"
                onError={() => hideBrokenPhoto(item.id)}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                data-testid={`home-favorite-image-${item.id}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <p
                  className="text-sm text-white/75"
                  data-testid={`home-favorite-category-${item.id}`}
                >
                  {item.category}
                </p>
                <h3
                  className="font-display text-2xl font-bold"
                  data-testid={`home-favorite-name-${item.id}`}
                >
                  {item.name}
                </h3>
                <p className="mt-1 text-sm text-white/85">Order fresh →</p>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 flex justify-end">
          <Link to="/menu">
            <Button
              variant="link"
              className="px-0 text-[hsl(var(--primary))]"
              data-testid="home-favorites-view-menu"
            >
              View full menu <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
